import { gql } from '@apollo/client'
import creditCardType from 'credit-card-type'
import { PAYMENT_METHOD } from 'lib/core/resources/constants'

export const commerceHubPaymentTokenCreate = gql`
  mutation commerceHubPaymentTokenCreate(
    $ccNumber: String!
    $ccCid: String!
    $ccType: String!
    $ccExpMonth: String!
    $ccExpYear: String!
    $cardholderName: String
  ) {
      commerceHubPaymentTokenCreate(
          input: {
           cc_number: $ccNumber,
           cc_cid: $ccCid,
           cc_type: $ccType,
           cc_exp_month: $ccExpMonth,
           cc_exp_year: $ccExpYear,
           cardholder_name: $cardholderName
        }
      ) {
      public_hash
      payment_method_code
      details
    }
  }
`

export const commerceHubPaymentTokenDelete = gql`
  mutation deletePaymentToken(
    $publicHash: String!
  ) {
    deletePaymentToken(
        public_hash: $publicHash,
    ) {
      result
    }
  }
`

export interface CommerceHubPaymentToken {
  cc_number: string
  cc_cid: string
  cc_type: string
  cc_exp_month: string
  cc_exp_year: string
  cardholder_name: string
}

export interface DeletePaymentTokenOut {
  result: Boolean
}

export interface SetVaultPaymentMethodOnCartResponse {
  setPaymentMethodOnCart: {
    cart: {
      selected_payment_method: {
        code: string
      }
    }
  }
}

export const setVaultPaymentMethodOnCartMutation = gql`
  mutation setPaymentMethodOnCart(
    $cartId: String!
    $publicHash: String!
  ) {
    setPaymentMethodOnCart(
      input: {
        cart_id: $cartId,
        payment_method: {
          code: "${PAYMENT_METHOD.COMMERCE_HUB_VAULT}"
          commerce_hub_vault: {
            public_hash: $publicHash
          }
        }
      }
    ) {
      cart {
        selected_payment_method {
          code
          title
        }
      }
    }
  }
`

export const setCardPaymentMethodOnCartMutation = gql`
  mutation setPaymentMethodOnCart(
    $cartId: String!
    $ccNumber: String!
    $ccCid: String!
    $ccType: String!
    $ccExpMonth: String!
    $ccExpYear: String!
    $cardholderName: String
  ) {
    setPaymentMethodOnCart(
      input: {
        cart_id: $cartId,
        payment_method: {
          code: "${PAYMENT_METHOD.COMMERCE_HUB}"
          commerce_hub: {
            cc_number: $ccNumber,
            cc_cid: $ccCid,
            cc_type: $ccType,
            cc_exp_month: $ccExpMonth,
            cc_exp_year: $ccExpYear,
            cardholder_name: $cardholderName
            is_active_payment_token_enabler: true
          }
        }
      }
    ) {
      cart {
        selected_payment_method {
          code
          title
        }
      }
    }
  }
`

export const setApplePayPaymentMethodOnCartMutation = gql`
  mutation setPaymentMethodOnCart(
    $cartId: String!
    $paymentData: String!
    $displayName: String
    $network: String
    $transactionId: String
    $saveCard: Boolean
  ) {
    setPaymentMethodOnCart(
      input: {
        cart_id: $cartId,
        payment_method: {
          code: "${PAYMENT_METHOD.COMMERCE_HUB_APPLE_PAY}"
          commerce_hub_apple_pay: {
            payment_data: $paymentData
            display_name: $displayName
            network: $network
            transaction_id: $transactionId
            is_active_payment_token_enabler: $saveCard
          }
        }
      }
    ) {
      cart {
        selected_payment_method {
          code
          title
        }
      }
    }
  }
`

export const setGooglePayPaymentMethodOnCartMutation = gql`
  mutation setPaymentMethodOnCart(
    $cartId: String!
    $token: String!
    $displayName: String
    $network: String
    $saveCard: Boolean
  ) {
    setPaymentMethodOnCart(
      input: {
        cart_id: $cartId,
        payment_method: {
          code: "${PAYMENT_METHOD.COMMERCE_HUB_GOOGLE_PAY}"
          commerce_hub_google_pay: {
            token: $token
            display_name: $displayName
            network: $network
            is_active_payment_token_enabler: $saveCard
          }
        }
      }
    ) {
      cart {
        selected_payment_method {
          code
          title
        }
      }
    }
  }
`

// Values the backend records under `walletType` in the vault token details
// (Bluprintx\CommerceHub\Gateway\Response\VaultTokenHandler).
export const WALLET_TYPE = {
  APPLE_PAY: 'ApplePay',
  GOOGLE_PAY: 'GooglePay'
}

/**
 * Human readable name of the digital wallet a stored credential originates from.
 * Empty string for a directly entered card.
 */
export const getWalletLabel = (details?: Record<string, any>): string => {
  switch (details?.walletType) {
    case WALLET_TYPE.APPLE_PAY:
      return 'Apple Pay'
    case WALLET_TYPE.GOOGLE_PAY:
      return 'Google Pay'
    default:
      return ''
  }
}

/**
 * Label of a stored credential.
 *
 * Every detail is optional on purpose: a credential stored from Apple Pay / Google Pay
 * only carries what the wallet charge response happened to report about the underlying
 * card, so brand, expiration date and cardholder name can all be absent. Missing parts
 * are dropped instead of rendering as "undefined".
 */
export const getStoredCardLabel = (details?: Record<string, any>): string => {
  const card = [details?.type, details?.maskedCc].filter(Boolean).join(' - ')
  const cardWithExpiry = details?.expirationDate ? `${card} (${details.expirationDate})` : card

  const label = [cardWithExpiry, details?.cardholderName, getWalletLabel(details)]
    .filter(Boolean)
    .join(' - ')

  return label || 'Saved card'
}

// Short code mapping specific to CommerceHub requirements
const CARD_TYPE_SHORT_CODE: Record<string, string> = {
  visa: 'VI',
  mastercard: 'MC',
  'american-express': 'AE',
  'diners-club': 'DC',
  discover: 'DI'
}

/**
 * Derive CommerceHub short credit card type code from a card number.
 * Returns empty string if type cannot be determined or not mapped.
 */
export const deriveCcType = (ccNumber: string): string => {
  if (!ccNumber) return ''
  const rawType = creditCardType(ccNumber)?.[0]?.type
  return rawType ? (CARD_TYPE_SHORT_CODE[rawType] || '') : ''
}
